import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ExpenseReportConfig } from './ExpenseReportConfig';
import ReportTableRow from './ReportTableRow';
import { Controller, useForm } from 'react-hook-form';
import { Expense } from '../../../interfaces/Expense.interface';
import { ReportModalProps } from '../../../interfaces/ReportModalProps.interface';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { useSelectedOrganization } from '../../../../../store/selectors';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { UserRole } from '../../../../users/enums/UserRole.enum';
import { formatCurrency } from '../../../../../common/helpers/format.helper';
import { useTranslation } from 'react-i18next';
import { ANAF_HELPER_LINK } from '../../../../../common/constants/constants';

const ExpenseReportModal = ({
  onClose,
  readonly,
  total,
  year,
  defaultValue,
  onSave,
}: ReportModalProps) => {
  const [totalDefalcat, setTotalDefalcat] = useState<number>(0);
  const [isReadonly, setIsReadonly] = useState<boolean>(readonly || false);
  const { organizationGeneral } = useSelectedOrganization();
  const { role } = useContext(AuthContext);
  const { t } = useTranslation(['financial', 'organization', 'common']);

  // form
  const {
    control,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // init form values
  useEffect(() => {
    if (defaultValue) {
      reset({ ...defaultValue });
      recalculate(defaultValue as Partial<Expense>);
    }
  }, [defaultValue]);

  const recalculate = (values: Partial<Expense>) => {
    const newTotal = Object.values(values).reduce(
      (prev: number, current: number) => (prev += +current || 0),
      0,
    );
    setTotalDefalcat(Math.round((newTotal + Number.EPSILON) * 100) / 100);
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative bg-white rounded-lg p-6 sm:p-10 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-3xl sm:w-full">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    aria-label={t('modal.close', { ns: 'organization' })}
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => onClose()}
                  >
                    <span className="sr-only">{t('modal.close', { ns: 'organization' })}</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="sm:text-lg lg:text-xl text-md leading-6 font-bold text-gray-900"
                    >
                      {`${t('modal.expense_report')} ${year}`}
                    </Dialog.Title>
                    <div className="mt-4">
                      <p className="sm:text-sm lg:text-base text-xs text-gray-500">
                        {t('modal.expense_report_information')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex flex-row-reverse">
                  <span className="sm:text-lg lg:text-xl text-md text-gray-900 font-bold leading-6">
                    {total !== null && total !== undefined ? `${formatCurrency(total)} RON` : 'N/A'}
                  </span>
                  <span className="sm:text-lg lg:text-xl text-md text-gray-400 font-normal leading-6 px-3">
                    {t('modal.expense')}
                  </span>
                </div>
                <a
                  aria-label={t('modal.anaf')}
                  href={`${ANAF_HELPER_LINK}`}
                  target="_blank"
                  className="mt-4 sm:text-sm lg:text-base text-xs leading-4 font-normal text-gray-400 text-right flex flex-row-reverse"
                  rel="noreferrer"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-1 text-gray-400 cursor-pointer" />
                  {t('modal.anaf')}
                </a>
                <form className="sm:overflow-auto overflow-scroll">
                  <table className="min-w-full divide-y divide-gray-300 mt-8">
                    <thead className="bg-gray-100">
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left font-titilliumBold sm:text-sm lg:text-base text-xs font-bold text-gray-800 sm:pl-6"
                        >
                          {t('modal.category_expense')}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left font-titilliumBold sm:text-sm lg:text-base text-xs font-bold text-gray-800"
                        >
                          {t('modal.sum')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 overflow-scroll">
                      {Object.getOwnPropertyNames(ExpenseReportConfig).map((name) => (
                        <Controller
                          key={ExpenseReportConfig[name].key}
                          name={ExpenseReportConfig[name].key}
                          rules={ExpenseReportConfig[name].rules}
                          control={control}
                          render={({ field: { onChange, value } }) => {
                            const { label, info, ...config } = ExpenseReportConfig[name].config;
                            return (
                              <ReportTableRow
                                label={label}
                                info={info}
                                config={{
                                  ...config,
                                  name: ExpenseReportConfig[name].key,
                                  error: errors[ExpenseReportConfig[name].key]?.message,
                                  defaultValue: value,
                                  onChange: onChange,
                                  onBlur: () => recalculate(getValues() as Expense),
                                }}
                                readonly={isReadonly}
                              />
                            );
                          }}
                        />
                      ))}
                      <tr>
                        <td className="whitespace-nowrap py-7 pl-4 pr-3 sm:text-sm lg:text-base text-xs font-bold text-gray-700 sm:pl-6">
                          {t('modal.defalcat')}
                        </td>
                        <td className="sm:whitespace-nowrap py-4 px-3 sm:text-sm lg:text-base text-xs font-bold ">
                          {`${totalDefalcat} RON`}
                          {total !== null && total !== undefined && totalDefalcat !== total && (
                            <span className="font-medium text-red-600">
                              {total > totalDefalcat
                                ? `(${formatCurrency(total - totalDefalcat)} RON ${t(
                                    'modal.unallocated',
                                  )})`
                                : `(${formatCurrency(totalDefalcat - total)} RON ${t(
                                    'modal.excess',
                                  )})`}
                            </span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </form>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  {!isReadonly && (
                    <>
                      <button
                        aria-label={t('modal.save', { ns: 'organization' })}
                        type="button"
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-500 sm:text-sm lg:text-base text-xs font-medium text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto"
                        onClick={handleSubmit(onSave)}
                      >
                        {t('modal.save', { ns: 'organization' })}
                      </button>
                      <button
                        aria-label={t('modal.cancel', { ns: 'organization' })}
                        type="button"
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white sm:text-sm lg:text-base text-xs font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto"
                        onClick={() => onClose()}
                      >
                        {t('modal.cancel', { ns: 'organization' })}
                      </button>
                    </>
                  )}
                  {isReadonly && role !== UserRole.EMPLOYEE && (
                    <button
                      aria-label={t('edit', { ns: 'common' })}
                      type="button"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-500 sm:text-sm lg:text-base text-xs font-medium text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto"
                      onClick={() => {
                        setIsReadonly(false);
                      }}
                    >
                      {t('edit', { ns: 'common' })}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ExpenseReportModal;
